import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useBlocker } from "react-router-dom";
import { isEmpty, isEqual, pick, omit, cloneDeep } from "lodash";
import useZustandStore from "zustandStore";

// onlyCompareList : Only compare these props.
// e.g. ["name", "security"];

// excludeCompareList : Exclude these props for comparing.
// e.g. ["name", "security"];

export const useUnsavedChangesBlocker = ({
  onlyCompareList = [],
  excludeCompareList = [],
} = {}) => {
  const [snapshot, setSnapshot] = useState(null);
  const [currentData, setCurrentData] = useState(null);

  const [isTabBlocked, setIsTabBlocked] = useState(false);
  const pendingAction = useRef(null);

  const setGlobalIsDataChanged = useZustandStore(state => state.setGlobalIsDataChanged);
  const setGlobalOnApplyFn = useZustandStore(state => state.setGlobalOnApplyFn);
  const setGlobalOnDiscardFn = useZustandStore(state => state.setGlobalOnDiscardFn);

  const finalWhiteList = useMemo(() => {
    return onlyCompareList.filter(key => !excludeCompareList.includes(key));
  }, [onlyCompareList, excludeCompareList]);

  const safeClone = useCallback((data) => {
    if (data == null) return null;

    return data.map(item => {
      let processedItem = item;

      if (!isEmpty(excludeCompareList)) processedItem = omit(processedItem, excludeCompareList);
      if (!isEmpty(finalWhiteList)) processedItem = pick(processedItem, finalWhiteList);

      return cloneDeep(processedItem);
    });
  }, [excludeCompareList, finalWhiteList]);

  const isDataChanged = useMemo(() => {
    if (snapshot === null || currentData === null) return false;
    return !isEqual(currentData, snapshot);
  }, [snapshot, currentData]);

  const routeBlocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!isDataChanged) return false;

    const currentUrl = `${currentLocation.pathname}${currentLocation.search}`;
    const nextUrl = `${nextLocation.pathname}${nextLocation.search}`;

    return currentUrl !== nextUrl;
  });

  const tabBlocker = useCallback((fn) => {
    return (...args) => {
      if (isDataChanged) {
        pendingAction.current = () => fn(...args); // Save the action first.
        setIsTabBlocked(true);
        return;
      }
      return fn(...args);
    };
  }, [isDataChanged]);

  const onInitBlockerData = useCallback(data => {
    const cleanData = safeClone(data);
    setCurrentData(cleanData);
    setSnapshot(cleanData);
  }, [safeClone]);

  const onUpdateBlockerData = useCallback(data => {
    setCurrentData(safeClone(data));
  }, [safeClone]);

  const onClearBlockerData = useCallback(() => {
    setSnapshot(null);
    setCurrentData(null);
    setIsTabBlocked(false);

    setGlobalOnApplyFn(null);
    setGlobalOnDiscardFn(null);

    pendingAction.current = null;
  }, [setGlobalOnApplyFn, setGlobalOnDiscardFn]);

  // Discard the block.
  const onBlockerDiscard = useCallback(() => {
    if (routeBlocker.state === "blocked") {
      routeBlocker.proceed(); // Reset the Route blocker.
    } else if (isTabBlocked) {
      pendingAction.current?.();
      pendingAction.current = null;
      setIsTabBlocked(false); // Turn off the Tab blocker.
    }

    if (snapshot !== null) {
      onInitBlockerData(snapshot);
    }
  }, [routeBlocker, isTabBlocked, snapshot, onInitBlockerData]);

  // Reset the blocker.
  const onBlockerApply = useCallback(() => {
    if (routeBlocker.state === "blocked") {
      routeBlocker.reset(); // Reset the Route blocker.
    }
    pendingAction.current = null;
    setIsTabBlocked(false); // Turn off the Tab blocker.
  }, [routeBlocker]);

  useEffect(() => {
    setGlobalIsDataChanged(isDataChanged);
  }, [isDataChanged, setGlobalIsDataChanged]);

  useEffect(() => {
    return () => {
      onClearBlockerData();
      setGlobalIsDataChanged(false);
    }
  }, [setGlobalIsDataChanged, onClearBlockerData]);

  return {
    isDataChanged,
    isUnsavedBlocked: routeBlocker.state === "blocked" || isTabBlocked,
    onClearBlockerData,
    onInitBlockerData,
    onUpdateBlockerData,
    tabBlocker,
    onBlockerApply,
    onBlockerDiscard,
  };
};